import {
    createContext,
    useContext,
    useMemo,
    ReactNode,
} from 'react';

import { useAuth } from 'context/AuthContext';
import { useGetUserById } from 'hooks/useUsers';

/* =========================
   Types
========================= */

type ProfileContextType = {
    positions: any[];
    experiences: any[];
    educations: any[];
    reimbursement: any;
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
};

type ProfileProviderProps = {
    children: ReactNode;
};

/* =========================
   Context
========================= */

const ProfileContext = createContext<ProfileContextType | undefined>(
    undefined
);

/* =========================
   Provider
========================= */

export function ProfileProvider({ children }: ProfileProviderProps) {
  const { user } = useAuth();

  const {
    data,
    refetch,
    isLoading,
    isFetching,
  } = useGetUserById(user?.id, {
    enabled: !!user?.id,
    refetchOnWindowFocus: false,
    keepPreviousData: true,
  });

  const value = useMemo(
    () => ({
      positions: data?.current_positions ?? [],
      experiences: data?.user_experiences ?? [],
      educations: data?.user_educations ?? [],
      reimbursement: data?.reimbursement ?? null,
      isLoading,
      isFetching,
      refetch,
    }),
    [data, isLoading, isFetching, refetch]
  );

  return (
    <ProfileContext.Provider value={value}>
      {children}
    </ProfileContext.Provider>
  );
}


/* =========================
   Hook
========================= */

export function useProfileContext() {
    const context = useContext(ProfileContext);

    if (!context) {
        throw new Error(
            'useProfileContext must be used within a ProfileProvider'
        );
    }

    return context;
}
